/**
 * PDP rewards line — fills snippets/pdp-rewards-points.liquid with the points
 * the shopper's selection earns: from Birch Rewards (assets/birch-rewards.js,
 * OM's program) once it's Live, otherwise from Smile.js as before.
 *
 * Birch Rewards: points = spend × the program's points per $1, rounded the way
 * OM's engine rounds (a $899 crib at 5/$1 earns 4495). What the shopper
 * already has takes that sentence's place when there is some:
 *   - signed in: OM's balance (/rewards/me), when checkout would use it (at
 *     least the program's minPoints, account not blocked);
 *   - signed out: the orders this browser placed, from the birch_rw_orders
 *     cookie OM's rewards-pixel sets at checkout ("v1~<orderId>.<cents>~…",
 *     money only), each turned into points like OM's engine does, with a
 *     sign-in link. A signed-in member's visit drops the cookie — their real
 *     balance has taken over.
 * Neither one: just the earn sentence, as before.
 *
 * Smile:
 * Points = whole spend steps × the program's per-order earning rule
 * (Place an order: 5 Points per $1 → a $899 crib earns 4495). The rule is read
 * live with Smile.earningRules.get(), which needs no customer, and the line
 * stays hidden until it arrives.
 *
 * Spend = variant price × quantity + ticked add-ons. `variant:update` brings
 * the new variant (its price in cents), `quantity-selector:update` the
 * stepper, and plain `change`/`input` catch typed quantities and the add-on
 * checkboxes. A combined-listing sibling switch morphs the whole product block
 * (assets/variant-picker.js) — this line included, back to its hidden server
 * markup — so the listeners sit on the document, the line is looked up afresh
 * each time, and only events from the line's own section count (a quick-add
 * dialog fires the same events elsewhere on the page).
 *
 * Plain IIFE, matching assets/rewards-landing.js.
 */

'use strict';

(function () {
  var READY_TIMEOUT_MS = 8000;

  var rule = null;
  var started = false;
  var birch = null; // OM's live program (assets/birch-rewards.js), when there is one
  var BIRCH_WAIT_MS = 3000;
  var have = null; // { member, points, cents, many } — points the shopper already has
  var ORDERS_COOKIE = 'birch_rw_orders';

  function smile() {
    var S = window.Smile;
    return S && S.earningRules && S.pointsSettings ? S : null;
  }

  // The rule that pays per amount spent; tier-restricted rules aren't the
  // program everyone joins (same filter as rewards-landing.js).
  function spendRule(rules) {
    return (rules || []).filter(function (r) {
      return !(r.restrictedToVipTierIds || []).length &&
        r.rewardValue && r.rewardValue.type === 'variable' && r.rewardValue.variable &&
        r.rewardValue.variable.value > 0 && r.rewardValue.variable.perAmount > 0;
    })[0] || null;
  }

  // The plain number with the program's own points name ("4495 Points").
  // Smile.formatPoints would group the digits ("4,495"), which the PDP doesn't.
  function fmt(n) {
    var label = null;
    try { label = window.Smile.pointsSettings.preloaded().pointsLabel; } catch (_e) { /* not loaded */ }
    var name = label ? (n === 1 ? label.one : label.other) : (n === 1 ? 'Point' : 'Points');
    return n + ' ' + name;
  }

  function line() {
    return document.querySelector('[data-pdp-rewards-points]');
  }

  function sectionOf(el) {
    return el.closest('.shopify-section') || document.body;
  }

  // Only events from inside the line's own section move the numbers.
  function ownSection(e) {
    var root = line();
    return !!root && e.target instanceof Element && sectionOf(e.target) === sectionOf(root);
  }

  function quantity(section) {
    var input = section.querySelector('input[name="quantity"]');
    var q = input ? parseInt(input.value, 10) : 1;
    return q > 0 ? q : 1;
  }

  function addonsCents(section) {
    var sum = 0;
    section.querySelectorAll('.addons .card__input:checked').forEach(function (input) {
      sum += parseInt(input.getAttribute('data-price'), 10) || 0;
    });
    return sum;
  }

  // [{ cents }] from the pixel's cookie; anything malformed is dropped.
  function cookieOrders() {
    var m = document.cookie.match(new RegExp('(?:^|; )' + ORDERS_COOKIE + '=([^;]*)'));
    var parts = m ? m[1].split('~') : [];
    if (parts.shift() !== 'v1') return [];
    return parts.map(function (p) {
      var o = /^(\d+)\.(\d+)$/.exec(p);
      return o ? { cents: parseInt(o[2], 10) } : null;
    }).filter(Boolean);
  }

  function dropCookie() {
    document.cookie = ORDERS_COOKIE + '=; path=/; max-age=0; SameSite=Lax; Secure';
  }

  function loadHave(p) {
    var B = window.BirchRewards;
    var root = line();
    if (!root) return;
    if (root.getAttribute('data-signed-in') === 'true') {
      B.me().then(function (m) {
        if (!m) return;
        dropCookie();
        if (m.blocked || !(m.points >= Math.max(1, p.minPoints || 0))) return;
        have = { member: true, points: m.points, cents: m.valueCents };
        render();
      });
      return;
    }
    var orders = cookieOrders();
    var points = orders.reduce(function (sum, o) { return sum + B.earnPoints(o.cents, p); }, 0);
    if (points > 0) have = { member: false, points: points, cents: B.valueCents(points, p), many: orders.length > 1 };
  }

  function renderHave(root) {
    var el = root.querySelector('[data-pdp-rewards-have]');
    if (!el) return;
    var earn = root.querySelector('[data-pdp-rewards-earn]');
    var showing = !!(birch && have);
    el.hidden = !showing;
    if (earn) earn.hidden = showing;
    if (!showing) return;
    var B = window.BirchRewards;
    var amount = '<strong>' + B.formatPoints(have.points, { group: false }) + '</strong> (' + B.formatMoney(have.cents) + ')';
    if (have.member) {
      el.innerHTML = 'You have ' + amount + ' to use at checkout.';
    } else {
      var a = document.createElement('a');
      a.href = root.getAttribute('data-sign-in-url') || '/customer_authentication/login';
      a.textContent = 'Sign in';
      el.innerHTML = (have.many ? 'Your past orders' : 'Your last order') + ' may have earned you ' + amount + '. ' + a.outerHTML + ' to use them.';
    }
  }

  function render() {
    var root = line();
    if ((!rule && !birch) || !root) return;
    var section = sectionOf(root);
    var cents = (parseInt(root.getAttribute('data-price'), 10) || 0) * quantity(section) + addonsCents(section);
    var points;
    if (birch) {
      points = window.BirchRewards.earnPoints(cents, birch);
    } else {
      var v = rule.rewardValue.variable;
      points = Math.floor(cents / 100 / v.perAmount) * v.value;
    }
    root.querySelector('[data-pdp-rewards-amount]').textContent = birch ? window.BirchRewards.formatPoints(points, { group: false }) : fmt(points);
    renderHave(root);
    root.hidden = points <= 0;
  }

  // Birch Rewards first; Smile only while OM's program isn't Live (or the
  // client never loads).
  function whenRewardsReady() {
    var done = false;
    function useSmile() {
      if (done) return;
      done = true;
      whenSmileReady();
    }
    function ask(B) {
      if (done || !B) return;
      B.program().then(function (p) {
        if (done) return;
        if (!p) return useSmile();
        done = true;
        birch = p;
        loadHave(p);
        render();
      }, useSmile);
    }
    if (window.BirchRewards) ask(window.BirchRewards);
    else document.addEventListener('birch-rewards:ready', function () { ask(window.BirchRewards); }, { once: true });
    setTimeout(function () { if (!window.BirchRewards) useSmile(); }, BIRCH_WAIT_MS);
  }

  // A signed-in customer Smile has excluded from the program earns nothing.
  function excluded() {
    var root = line();
    if (!root || root.getAttribute('data-signed-in') !== 'true') return false;
    try {
      var c = window.Smile.customer.current();
      return !!c && c.state === 'disabled';
    } catch (_e) {
      return false;
    }
  }

  function load() {
    var S = smile();
    if (!S || started) return;
    started = true;

    // formatPoints needs pointsSettings preloaded; a guest has nothing preloaded.
    var settingsReady;
    try {
      settingsReady = S.pointsSettings.preloaded() ? null : S.preload(['pointsSettings']);
    } catch (_e) {
      settingsReady = S.preload(['pointsSettings']);
    }

    Promise.all([
      settingsReady ? settingsReady.catch(function () { return null; }) : null,
      S.earningRules.get(),
    ]).then(function (r) {
      rule = excluded() ? null : spendRule(r[1]);
      render();
    }, function () { /* Smile unavailable: the line stays hidden */ });
  }

  // Smile's app embed initializes Smile.js on its own schedule; this script can
  // run before or after `smile-js-initialized`, so it also probes once.
  function whenSmileReady() {
    document.addEventListener('smile-js-initialized', load, { once: true });
    var S = smile();
    if (S) {
      try {
        S.pointsSettings.get().then(load, function () { /* not ready yet */ });
      } catch (_e) { /* not ready yet */ }
    }
    setTimeout(load, READY_TIMEOUT_MS);
  }

  function init() {
    if (!line()) return;

    // Runs after the morph, so the line it finds is the re-rendered one.
    document.addEventListener('variant:update', function (e) {
      if (!ownSection(e)) return;
      var variant = e.detail && e.detail.resource;
      if (variant && variant.price != null) line().setAttribute('data-price', variant.price);
      render();
    });
    ['quantity-selector:update', 'change', 'input'].forEach(function (type) {
      document.addEventListener(type, function (e) {
        if (ownSection(e)) render();
      });
    });

    whenRewardsReady();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
